import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import Icon from 'shopper/components/Icon';

import { toOfferLink } from 'lib/links';

const VOTE = { LIKE: 'like', DISLIKE: 'dislike' };

const ReactionGroup = ({
  likes = 0,
  userVote = null,
  serverVote = null,
  commentsCount = 0,
  offerSlug = null,
  onLike = () => {},
  onDislike = () => {},
  showDislike = true,
  isLoggedIn = true,
}) => {
  const [vote, setVote] = useState(userVote);
  const [animating, setAnimating] = useState(null);

  // Sync with external vote state — covers both pre-fill and failure revert.
  useEffect(() => {
    setVote(userVote);
  }, [userVote]);

  const triggerVote = useCallback(
    (type) => {
      // Fire callback first — parent gates via runCallbackIfLoggedIn and pops
      // the login modal if the user is anonymous.
      if (type === VOTE.LIKE) onLike();
      else onDislike();

      if (!isLoggedIn) return;

      const next = vote === type ? null : type;
      setVote(next);
      setAnimating(type);
      setTimeout(() => setAnimating(null), 400);
    },
    [vote, onLike, onDislike, isLoggedIn]
  );

  const isLiked = vote === VOTE.LIKE;
  const isDisliked = vote === VOTE.DISLIKE;

  // `likes` from the server already includes the user's pre-existing vote (if any).
  // Use the frozen `serverVote` (captured at first query resolution, never mutated by
  // handleVote) so that casting a new vote doesn't change initialDelta and cancel the
  // optimistic currentDelta (self-canceling bug).
  const initialDelta =
    serverVote === VOTE.LIKE ? 1 : serverVote === VOTE.DISLIKE ? -1 : 0;
  const currentDelta = isLiked ? 1 : isDisliked ? -1 : 0;
  const count = likes + currentDelta - initialDelta;

  return (
    <div className="flex items-center gap-2">
      {/* Pill like/dislike */}
      <div className="inline-flex items-center overflow-hidden rounded-full border border-neutral-high-200 bg-neutral-high-100 transition-all duration-300 ease-in-out dark:border-0 dark:bg-surface-raised">
        {/* Botão like */}
        {!isDisliked && (
          <button
            aria-label="Gostei"
            className="flex cursor-pointer items-center justify-center px-2 py-1.5 transition-colors hover:bg-neutral-high-200 dark:hover:bg-surface-raised-hover"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              triggerVote(VOTE.LIKE);
            }}
          >
            <Icon
              className={twJoin(
                'transition-colors',
                animating === VOTE.LIKE && 'animate-vote-bounce',
                isLiked
                  ? 'text-primary-300 dark:text-primary-200'
                  : 'text-neutral-high-500 dark:text-neutral-high-300'
              )}
              name={isLiked ? 'like-filled' : 'like'}
              size="size3"
            />
          </button>
        )}

        {/* Contagem */}
        <span
          className={twJoin(
            'cursor-text select-text px-1.5 text-sm font-medium transition-colors duration-300',
            isLiked && 'font-bold text-primary-300 dark:text-primary-200',
            isDisliked &&
              'font-bold text-highlight-300 dark:text-highlight-200',
            !isLiked &&
              !isDisliked &&
              'text-neutral-low-300 dark:text-neutral-high-300'
          )}
        >
          {count}
        </span>

        {/* Botão dislike — oculto quando o usuário já deu like */}
        {showDislike && !isLiked && (
          <button
            aria-label="Não gostei"
            className="flex cursor-pointer items-center justify-center px-2 py-1.5 transition-colors hover:bg-neutral-high-200 dark:hover:bg-surface-raised-hover"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              triggerVote(VOTE.DISLIKE);
            }}
          >
            <Icon
              className={twJoin(
                'transition-colors',
                animating === VOTE.DISLIKE && 'animate-vote-bounce',
                isDisliked
                  ? 'text-highlight-300 dark:text-highlight-200'
                  : 'text-neutral-high-500 dark:text-neutral-high-300'
              )}
              name={isDisliked ? 'dislike-filled' : 'dislike'}
              size="size3"
            />
          </button>
        )}
      </div>

      {/* Pill comentários — linked when offerSlug is provided (shows count or 'Dúvidas?');
          non-link span when null (card already inside <a>) — always rendered. */}
      {offerSlug ? (
        <NextLink href={toOfferLink(offerSlug)} prefetch={false} passHref>
          <a
            aria-label={
              commentsCount > 0
                ? `${commentsCount} comentários`
                : 'Deixar uma dúvida'
            }
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors hover:bg-neutral-high-200 dark:hover:bg-surface-raised-hover"
          >
            <Icon
              className={twJoin(
                commentsCount > 0
                  ? 'text-neutral-low-300 dark:text-neutral-high-300'
                  : 'text-neutral-high-500 dark:text-neutral-high-300'
              )}
              name="chat"
              size="size3"
            />
            <span
              className={twJoin(
                'text-sm',
                commentsCount > 0
                  ? 'text-neutral-low-300 dark:text-neutral-high-300'
                  : 'text-neutral-high-500 dark:text-neutral-high-300'
              )}
            >
              {commentsCount > 0 ? commentsCount : 'Dúvidas?'}
            </span>
          </a>
        </NextLink>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors hover:bg-neutral-high-200 dark:hover:bg-surface-raised-hover">
          <Icon
            className={twJoin(
              commentsCount > 0
                ? 'text-neutral-low-300 dark:text-neutral-high-300'
                : 'text-neutral-high-500 dark:text-neutral-high-300'
            )}
            name="chat"
            size="size3"
          />
          <span
            className={twJoin(
              'text-sm',
              commentsCount > 0
                ? 'text-neutral-low-300 dark:text-neutral-high-300'
                : 'text-neutral-high-500 dark:text-neutral-high-300'
            )}
          >
            {commentsCount > 0 ? commentsCount : 'Dúvidas?'}
          </span>
        </span>
      )}
    </div>
  );
};

ReactionGroup.propTypes = {
  likes: PropTypes.number,
  userVote: PropTypes.oneOf(['like', 'dislike', null]),
  commentsCount: PropTypes.number,
  offerSlug: PropTypes.string,
  serverVote: PropTypes.oneOf(['like', 'dislike', null]),
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
  showDislike: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
};

export default ReactionGroup;
